<script>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import Banner from 'dashboard/components/ui/Banner.vue';
import { differenceInDays, isPast } from 'date-fns';

export default {
  components: { Banner },

  setup() {
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    const daysUntilExpiry = ref(null);
    const expiryStatus = ref(null);
    let refreshInterval = null;

    const account = computed(() =>
      store.getters['accounts/getAccount'](accountId.value)
    );

    const calculateExpiryInfo = () => {
      if (!account.value?.expires_at) {
        expiryStatus.value = null;
        daysUntilExpiry.value = null;
        return;
      }

      const expiresAt = new Date(account.value.expires_at);
      const gracePeriodDays = account.value.grace_period_days || 3;
      const gracePeriodEnd = new Date(
        expiresAt.getTime() + gracePeriodDays * 24 * 60 * 60 * 1000
      );
      const now = new Date();

      // 判断过期状态
      const isExpired = isPast(gracePeriodEnd);
      const isInGracePeriod = !isExpired && isPast(expiresAt);

      if (isExpired) {
        // 已过期（宽限期之后）
        expiryStatus.value = 'expired';
        daysUntilExpiry.value = differenceInDays(expiresAt, now);
      } else if (isInGracePeriod) {
        // 在宽限期内
        const daysUntilGraceEnd = differenceInDays(gracePeriodEnd, now);
        daysUntilExpiry.value = daysUntilGraceEnd;
        expiryStatus.value = 'in_grace_period';
      } else {
        // 计算距离到期的天数
        daysUntilExpiry.value = differenceInDays(expiresAt, now);

        if (daysUntilExpiry.value <= 3) {
          // 即将到期（3天内）
          expiryStatus.value = 'urgent';
        } else if (daysUntilExpiry.value <= 7) {
          // 即将到期（7天内）
          expiryStatus.value = 'warning';
        } else {
          // 不显示
          expiryStatus.value = null;
        }
      }
    };

    const bannerMessage = computed(() => {
      const days = daysUntilExpiry.value;

      if (expiryStatus.value === 'in_grace_period') {
        return store.$t('GENERAL_SETTINGS.EXPIRY_WARNING.GRACE_PERIOD_TITLE', {
          days,
        });
      }
      if (expiryStatus.value === 'expired') {
        return store.$t('GENERAL_SETTINGS.EXPIRY_WARNING.EXPIRED_TITLE');
      }
      if (expiryStatus.value === 'urgent') {
        return store.$t('GENERAL_SETTINGS.EXPIRY_WARNING.URGENT', { days });
      }
      if (expiryStatus.value === 'warning') {
        return store.$t('GENERAL_SETTINGS.EXPIRY_WARNING.TITLE', { days });
      }
      return '';
    });

    const colorScheme = computed(() => {
      // 已过期或在宽限期内：红色警示
      if (
        expiryStatus.value === 'expired' ||
        expiryStatus.value === 'in_grace_period'
      ) {
        return 'alert';
      }
      // 即将到期（3天内）：黄色警告
      if (expiryStatus.value === 'urgent') {
        return 'warning';
      }
      // 即将到期（7天内）：黄色警告
      if (expiryStatus.value === 'warning') {
        return 'warning';
      }
      return '';
    });

    const shouldShowBanner = computed(() => {
      return expiryStatus.value !== null;
    });

    const actionButtonLabel = computed(() => {
      return store.$t('GENERAL_SETTINGS.EXPIRY_WARNING.MANAGE_SUBSCRIPTION');
    });

    const routeToBilling = () => {
      const currentAccountId = accountId.value;
      // 使用路由跳转到套餐详情页面
      router.push({
        path: `/app/accounts/${currentAccountId}/subscription/plan`,
      });
    };

    onMounted(() => {
      calculateExpiryInfo();
      // 每小时刷新一次计算
      refreshInterval = setInterval(
        () => {
          calculateExpiryInfo();
        },
        60 * 60 * 1000
      );
    });

    onUnmounted(() => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    });

    return {
      shouldShowBanner,
      bannerMessage,
      colorScheme,
      actionButtonLabel,
      routeToBilling,
    };
  },
};
</script>

<template>
  <Banner
    v-if="shouldShowBanner"
    :color-scheme="colorScheme"
    :banner-message="bannerMessage"
    :action-button-label="actionButtonLabel"
    has-action-button
    @primary-action="routeToBilling"
  />
</template>
